import { BaseStore } from "$lib/stores/base.svelte";
import { api } from "$lib/helpers/api.service";
import type { PoiItem, PoiResponse } from "$lib/types";
import type { PlaceResult } from "$lib/types/place.types";
import { hungaryPlacesSearch } from "$lib/helpers/place-search.service";
import { filtersStore } from "./filters.svelte";

interface SearchParams {
  offset: number;
  limit: number;
  lat?: number;
  lng?: number;
  searchText?: string;
}

class SearchStore extends BaseStore {
  // POI results
  results = $state<PoiItem[]>([]);

  filteredSearchResults = $derived(
    this.results.filter((poi) => {
      return filtersStore.filters[poi.label]?.subcategories[poi.subCategory]
        ?.enabled;
    })
  );

  // Place/Town results
  placeResults = $state<PlaceResult[]>([]);

  searchText = $state("");
  hasMore = $state(true);
  placeHasMore = $state(true);
  placeOffset = $state(0);
  activeTab = $state<"pois" | "places">("pois");

  // Search both POIs and places
  async search(searchText: string, lat?: number, lng?: number, reset = true) {
    if (reset) {
      this.results = [];
      this.placeResults = [];
      this.hasMore = true;
    }

    this.searchText = searchText;

    // Search POIs
    const poiPromise = this.searchPois(searchText, lat, lng, reset);

    // Search places (only on initial search, not on load more)
    const placePromise = reset
      ? this.searchPlaces(searchText, true, 0, 10)
      : Promise.resolve();

    await Promise.all([poiPromise, placePromise]);
  }

  private async searchPois(
    searchText: string,
    lat?: number,
    lng?: number,
    reset = true
  ) {
    const activeSubcategories = filtersStore.getActiveSubcategories();
    const subCategoriesParam = activeSubcategories.join(",");
    const response = await this.executeWithLoading(
      () =>
        api.get<PoiResponse>("/point-of-interests/search", {
          offset: reset ? 0 : this.results.length,
          limit: 20,
          lat,
          lng,
          searchText: searchText.trim() || undefined,
          subCategories:
            subCategoriesParam.length > 0 ? subCategoriesParam : undefined,
        }),
      "Search failed"
    );

    function handleTags(tags: string[]) {
      if (!tags || tags.length < 1) return [];
      return tags
        .filter((tag) => !!tag.trim())
        .flatMap((tag) => tag.split(",").map((part) => part.trim()));
    }

    if (!response) {
      return 0;
    }

    const processedItems = response.items.map((item) => ({
      ...item,
      tags: handleTags(item.tags),
    }));

    this.results = reset
      ? processedItems
      : [...this.results, ...processedItems];

    // deduplicate items just in case...
    const ids = new Set();
    this.results = this.results.filter(({ id }) => !ids.has(id) && ids.add(id));

    this.hasMore = response.items.length === 20;

    if (reset) {
      this.isLoading = false;
    }

    return processedItems.filter((poi) => {
      return filtersStore.filters[poi.label]?.subcategories[poi.subCategory]
        ?.enabled;
    }).length;
  }

  private async searchPlaces(
    searchText: string,
    reset = true,
    offset = 0,
    limit = 10
  ) {
    if (!searchText.trim()) {
      this.placeResults = [];
      this.placeHasMore = false;
      return;
    }

    try {
      // Use local search
      let results = await hungaryPlacesSearch.search(searchText, limit, offset);

      results = this.mapResults(results);

      this.placeResults = reset ? results : [...this.placeResults, ...results];
      this.placeHasMore = results.length === limit;
      this.placeOffset = reset
        ? results.length
        : this.placeOffset + results.length;
    } catch (error) {
      console.error("Place search error:", error);
      this.placeResults = [];
    }
  }

  private mapResults(results: any[]): PlaceResult[] {
    function formatDisplayName(place: any): string {
      const parts = [place.name];

      if (place.type === "district" && place.districtNumber) {
        parts.push("Budapest");
      } else if (place.county && place.county !== place.name) {
        parts.push(place.county);
      }

      return parts.join(", ");
    }
    return results.map((place: any) => ({
      id: place.id,
      name: place.name,
      displayName: formatDisplayName(place),
      type: place.type,
      lat: place.lat,
      lon: place.lng || place.lon, // Handle both lng and lon
      address: {
        county: place.county,
        city: place.city,
        country: "Magyarország",
      },
      boundingbox: place.boundingbox || [],
    }));
  }

  async loadMore(lat?: number, lng?: number) {
    if (!this.hasMore || this.isLoading || this.activeTab !== "pois") return;
    let loadedNum = 0;
    do {
      loadedNum = await this.searchPois(this.searchText, lat, lng, false);
    } while (!loadedNum);
    this.isLoading = false;
  }

  async loadMorePlaces() {
    if (!this.placeHasMore || this.isLoading || this.activeTab !== "places")
      return;
    await this.searchPlaces(this.searchText, false, this.placeOffset, 10);
  }

  setActiveTab(tab: "pois" | "places") {
    this.activeTab = tab;
  }

  clearResults() {
    this.results = [];
    this.placeResults = [];
    this.searchText = "";
    this.error = null;
    this.hasMore = true;
  }
}

export const searchStore = new SearchStore();
